import * as styles from "./ProjectQuickview.css";

import { useAtomValue } from "jotai";
import type { JSX, ReactNode } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Button } from "@frontend/components/atoms/Button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerPopup,
  DrawerTitle,
} from "@frontend/components/atoms/Drawer";
import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import { Skeleton } from "@frontend/components/atoms/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { ContactPreviewPopover } from "@frontend/features/addressbook";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { formatAmount, parseAmount } from "@frontend/utils/amount";
import { Trans, useLingui } from "@lingui/react/macro";

import { projectQuickviewAtom } from "../stores/quickview";
import type {
  ProjectQuickview as Quickview,
  QuickviewAddress,
  QuickviewPerson,
} from "../types";
import {
  openHoursSummary,
  openHoursValidation,
  schedulerUrl,
} from "../utils/windows";

export type ProjectQuickviewProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (projectId: number) => void;
  /** Replays the tour of the panel; left out, the help button is not shown. */
  onHelp?: () => void;
};

/**
 * The drawer a row opens, the React counterpart of
 * `Subscription/quickview.html.twig`: enough of a project to decide whether to
 * open it. It stays non-modal, so the list behind it keeps working and moving
 * from row to row just refills the panel.
 */
export function ProjectQuickview({
  open,
  onOpenChange,
  onEdit,
  onHelp,
}: ProjectQuickviewProps): JSX.Element {
  const { t } = useLingui();

  const { project, loading, error } = useAtomValue(projectQuickviewAtom);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      swipeDirection="right"
      modal={false}
      disablePointerDismissal
    >
      <DrawerPopup size="lg" border="glass" backdrop={false}>
        <DrawerHeader>
          <DrawerTitle className={styles.title}>
            {project ? (
              <>
                <Avatar
                  size="md"
                  url={project.clientAvatarUrl ?? undefined}
                  fallback={<Icon name="Building2" size="sm" />}
                />

                <span className={styles.identity}>
                  <span className={styles.id}>#{project.id}</span>
                  <span className={styles.name}>{project.clientLabel}</span>
                </span>
              </>
            ) : (
              <Trans>Project</Trans>
            )}
          </DrawerTitle>

          <div className={styles.actions}>
            {project ? (
              <>
                {project.canValidateHours ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="md"
                          aria-label={t`Hours validation`}
                          onClick={() =>
                            openHoursValidation(project.id, t`Hours validation`)
                          }
                        >
                          <Icon name="Clock" size="sm" />
                        </Button>
                      }
                    />

                    <TooltipContent side="bottom">
                      {t`Hours validation`}
                    </TooltipContent>
                  </Tooltip>
                ) : null}

                {"intervention" === project.type ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="md"
                          aria-label={t`Show in the planning`}
                          render={<a href={schedulerUrl(project.id)} />}
                        >
                          <Icon name="CalendarDays" size="sm" />
                        </Button>
                      }
                    />

                    <TooltipContent side="bottom">
                      {t`Show in the planning`}
                    </TooltipContent>
                  </Tooltip>
                ) : null}

                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="md"
                        aria-label={t`Hours summary`}
                        onClick={() =>
                          openHoursSummary(project.id, t`Hours summary`)
                        }
                      >
                        <Icon name="FileText" size="sm" />
                      </Button>
                    }
                  />

                  <TooltipContent side="bottom">
                    {t`Hours summary`}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="md"
                        aria-label={t`Edit the project`}
                        onClick={() => onEdit(project.id)}
                      >
                        <Icon name="Pencil" size="sm" />
                      </Button>
                    }
                  />

                  <TooltipContent side="bottom">
                    {t`Edit the project`}
                  </TooltipContent>
                </Tooltip>
              </>
            ) : null}

            {onHelp ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="md"
                      aria-label={t`Help on this panel`}
                      onClick={onHelp}
                    >
                      <Icon name="CircleHelp" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="bottom">
                  {t`Help on this panel`}
                </TooltipContent>
              </Tooltip>
            ) : null}

            <DrawerClose />
          </div>
        </DrawerHeader>

        <DrawerContent className={styles.content}>
          {error ? (
            <p className={styles.alert} role="alert">
              {error}
            </p>
          ) : null}

          {null === project ? (
            loading ? (
              <Skeleton className={styles.placeholder} />
            ) : null
          ) : (
            <Body project={project} loading={loading} />
          )}
        </DrawerContent>
      </DrawerPopup>
    </Drawer>
  );
}

function Body({
  project,
  loading,
}: {
  project: Quickview;
  loading: boolean;
}): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const price = parseAmount(project.price);

  return (
    <div className={styles.body} data-loading={loading || undefined}>
      <section className={styles.section}>
        <Field label={t`Status`}>{project.statusLabel ?? "-"}</Field>

        <Field label={t`Department`}>
          {project.serviceGroupLabel ?? t`Unknown department`}
        </Field>

        <Field label={t`Supply number`}>
          {project.supply ? (
            <a
              className={styles.supply}
              href={project.supply.href}
              title={project.supply.statusLabel ?? undefined}
              target="_blank"
              rel="noopener"
            >
              <Icon name="Package" size="sm" />
              <span>
                {project.supply.id} {flag(project.supply.status)}
              </span>
            </a>
          ) : (
            <span className={styles.empty}>
              <Trans>No supply</Trans>
            </span>
          )}
        </Field>
      </section>

      {project.contract ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <Trans>Contract</Trans>
          </h3>

          <Field label={t`Start date`}>
            {project.contract.dateBegin ?? t`Undefined`}
          </Field>

          {project.contract.dateEnd ? (
            <Field label={t`End date`}>{project.contract.dateEnd}</Field>
          ) : null}

          {project.contract.durationLabel ? (
            <Field label={t`Duration`}>{project.contract.durationLabel}</Field>
          ) : null}

          <Field label={t`Signature date`}>
            {project.contract.dateSignature ?? t`Undefined`}
          </Field>

          <Field label={t`Automatic renewal`}>
            {project.contract.autoRenewal ? t`Yes` : t`No`}
          </Field>
        </section>
      ) : null}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Trans>Work location</Trans>
        </h3>

        {project.locationTypeLabel ? (
          <Field label={t`Place type`}>
            <span className={styles.type}>
              <Icon
                name={(project.locationTypeIcon ?? "MapPin") as IconName}
                size="sm"
              />
              {project.locationTypeLabel}
            </span>
          </Field>
        ) : null}

        <Field label={t`Work location`}>
          <Address address={project.locationAddress} empty={t`No place`} />
        </Field>

        <Field label={t`Delivery location`}>
          <Address address={project.deliveryAddress} empty={t`No place`} />
        </Field>

        <Field label={t`Billing address`}>
          <Address address={project.billingAddress} empty={t`No address`} />
        </Field>
      </section>

      <People
        title={t`In charge`}
        people={project.accountants}
        empty={t`Nobody is in charge of this project yet.`}
        previewLabel={t`See the details`}
      />

      <People
        title={t`Work posts`}
        people={project.jobs}
        empty={t`This project has no work post yet.`}
        previewLabel={t`See the details`}
      />

      {/* A price of its own belongs to a subscription: an intervention is
          billed from its balance sheet. */}
      {null === price ? null : (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <Trans>Price</Trans>
          </h3>

          <Field label={t`Subscription price`}>
            {`${formatAmount(price, locale)} CHF`}
          </Field>
        </section>
      )}
    </div>
  );
}

function People({
  title,
  people,
  empty,
  previewLabel,
}: {
  title: string;
  people: QuickviewPerson[];
  empty: string;
  previewLabel: string;
}): JSX.Element {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>

      {0 === people.length ? (
        <p className={styles.empty}>{empty}</p>
      ) : (
        people.map((person, index) => (
          <Field key={index} label={person.partLabel ?? ""}>
            <span className={styles.person}>
              <span>{person.personLabel ?? "-"}</span>

              {person.contactId ? (
                <ContactPreviewPopover
                  contactId={person.contactId}
                  side="left"
                  label={previewLabel}
                />
              ) : null}
            </span>
          </Field>
        ))
      )}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={styles.field}>
      <div className={styles.label}>{label}</div>
      <div className={styles.value}>{children}</div>
    </div>
  );
}

function Address({
  address,
  empty,
}: {
  address: QuickviewAddress | null;
  empty: string;
}): JSX.Element {
  if (null === address) {
    return <span className={styles.empty}>{empty}</span>;
  }

  const second = [address.extended, address.postOfficeBox]
    .filter(Boolean)
    .join(" ");

  const region = [address.state, address.country].filter(Boolean).join(" ");

  return (
    <div className={styles.address}>
      <div>{address.street}</div>

      {second ? <div>{second}</div> : null}

      <div>
        {address.postalCode} {address.city}
        {region ? ` / ${region}` : null}
      </div>
    </div>
  );
}

/** The two supply statuses the legacy list flags on the number itself. */
function flag(status: string | null): string {
  if ("1" === status) {
    return "(B)";
  }

  return "3" === status ? "(F)" : "";
}
